import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { PageMessage } from '../components/PageMessage'
import { useAuth } from '../auth/useAuth'
import {
  LIFECYCLE_PHASES,
  MILESTONE_ANCHORS,
  OWNER_ROLES,
  type LifecyclePhase,
  type MilestoneAnchor,
} from '../features/templates/phases'
import {
  groupByPhase,
  useAddMilestone,
  useDeleteMilestone,
  useDeleteTimelineTemplate,
  usePublishTimelineTemplate,
  useRetireTimelineTemplate,
  useTimelineTemplate,
  useUpdateMilestone,
  useUpdateTimelineTemplate,
  type Milestone,
} from '../features/templates/timelineApi'

interface MilestoneDraft {
  id?: string
  title: string
  phase: LifecyclePhase
  offsetDays: string
  anchor: MilestoneAnchor
  ownerRole: string
}

const emptyDraft: MilestoneDraft = {
  title: '',
  phase: 'SiteSetup',
  offsetDays: '-60',
  anchor: 'EventStart',
  ownerRole: '',
}

export function TimelineTemplateEditorPage() {
  const { templateId = '' } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const { data: template, isPending, isError, refetch } = useTimelineTemplate(templateId)

  const update = useUpdateTimelineTemplate(templateId)
  const publish = usePublishTimelineTemplate(templateId)
  const addMilestone = useAddMilestone(templateId)
  const updateMilestone = useUpdateMilestone(templateId)
  const deleteMilestone = useDeleteMilestone(templateId)
  const remove = useDeleteTimelineTemplate(templateId)
  const retire = useRetireTimelineTemplate(templateId)

  const [draft, setDraft] = useState<MilestoneDraft>(emptyDraft)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  if (isPending) return <p>Loading template…</p>
  if (isError || !template) {
    return <PageMessage title="Template not found" body="That timeline template does not exist." />
  }

  const isPublished = template.status === 'Published'
  const canEdit = can('Coordinator')
  const groups = groupByPhase(template.milestones)

  const report = (err: unknown) =>
    setError(err instanceof ApiError ? err.detail : 'That change could not be applied.')

  const submitMilestone = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setNotice(null)

    const offsetDays = Number.parseInt(draft.offsetDays, 10)
    if (Number.isNaN(offsetDays)) {
      setError('Offset must be a whole number of days.')
      return
    }

    const body = {
      title: draft.title.trim(),
      description: null,
      phase: draft.phase,
      offsetDays,
      anchor: draft.anchor,
      ownerRole: draft.ownerRole.trim() === '' ? null : draft.ownerRole.trim(),
    }

    try {
      if (draft.id) {
        await updateMilestone.mutateAsync({ id: draft.id, ...body })
      } else {
        await addMilestone.mutateAsync(body)
      }
      // The owner role and phase are kept for the next milestone. Milestones
      // are authored in runs that share both, and re-picking them every time is
      // exactly the repetition the dropdown exists to remove. Everything else
      // resets, because a title reused by accident is a real mistake.
      setDraft({ ...emptyDraft, ownerRole: draft.ownerRole, phase: draft.phase })
      await refetch()
    } catch (err) {
      report(err)
    }
  }

  const editExisting = (milestone: Milestone) =>
    setDraft({
      id: milestone.id,
      title: milestone.title,
      phase: milestone.phase,
      offsetDays: String(milestone.offsetDays),
      anchor: milestone.anchor,
      ownerRole: milestone.ownerRole ?? '',
    })

  return (
    <section>
      <div className="page-header">
        <div>
          <h1>{template.name}</h1>
          <div className="card-meta">
            <span>Version {template.version}</span>
            <span className={`status status-${template.status.toLowerCase()}`}>
              {template.status}
            </span>
            {template.isRetired ? <span className="badge">Retired</span> : null}
            {template.isUsedByAPlan ? <span className="badge">In use by a plan</span> : null}
          </div>
        </div>

        {/*
          Every action that changes this template's lifecycle lives here, next
          to the title. They used to be spread down the page - Publish at the
          top, the draft action inside a notice, Retire and Delete in a panel
          at the bottom - which made the destructive ones the hardest to reach
          on a page whose length grows with the template.
        */}
        <div className="header-actions">
          {canEdit && !isPublished ? (
            <button
              type="button"
              className="primary"
              disabled={template.milestones.length === 0 || publish.isPending}
              onClick={async () => {
                setError(null)
                try {
                  await publish.mutateAsync()
                  setNotice('Published. This version is now frozen and selectable for plans.')
                  await refetch()
                } catch (err) {
                  report(err)
                }
              }}
            >
              Publish
            </button>
          ) : null}

          {canEdit && isPublished ? (
            <button
              type="button"
              className="primary"
              disabled={update.isPending}
              onClick={async () => {
                setError(null)
                try {
                  const next = await update.mutateAsync({
                    name: template.name,
                    description: template.description,
                  })
                  navigate(`/templates/timeline/${next.id}`)
                } catch (err) {
                  report(err)
                }
              }}
            >
              Create draft version {template.version + 1}
            </button>
          ) : null}

          {can('Admin') ? (
            <>
              <button
                type="button"
                disabled={retire.isPending || template.isRetired}
                title="Stops this template being offered for new plans. Plans already built from it are untouched."
                onClick={async () => {
                  setError(null)
                  try {
                    await retire.mutateAsync()
                    setNotice('Retired. It is no longer offered when composing a plan.')
                    await refetch()
                  } catch (err) {
                    report(err)
                  }
                }}
              >
                Retire
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                title="Refused once any plan has been built from this template."
                onClick={async () => {
                  setError(null)

                  // Irreversible, and now one click from the top of the page
                  // rather than a scroll away.
                  if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) {
                    return
                  }

                  try {
                    await remove.mutateAsync()
                    navigate('/templates')
                  } catch (err) {
                    report(err)
                  }
                }}
              >
                Delete
              </button>
            </>
          ) : null}
        </div>
      </div>

      {error ? (
        <p role="alert" className="error">
          {error}
        </p>
      ) : null}
      {notice ? <p className="notice">{notice}</p> : null}

      {isPublished ? (
        <p className="notice">
          This version is published and frozen, so plans already built from it keep exactly what was
          published. Editing it creates the next draft.
        </p>
      ) : null}

      {/*
        Authoring left, result right - and authoring written first, so the
        single-column collapse needs no ordering trickery and a screen reader
        reaches the form before the list it adds to.
      */}
      <div className={canEdit && !isPublished ? 'editor-columns' : undefined}>
        {canEdit && !isPublished ? (
          <div className="editor-authoring">
            <form className="form" onSubmit={submitMilestone}>
              <h2>{draft.id ? 'Edit milestone' : 'Add milestone'}</h2>

              <div className="field">
                <label htmlFor="milestone-title">Title</label>
                <input
                  id="milestone-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  required
                />
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="milestone-phase">Phase</label>
                  <select
                    id="milestone-phase"
                    value={draft.phase}
                    onChange={(e) =>
                      setDraft({ ...draft, phase: e.target.value as LifecyclePhase })
                    }
                  >
                    {LIFECYCLE_PHASES.map((phase) => (
                      <option key={phase.value} value={phase.value}>
                        {phase.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="milestone-offset">Offset in days</label>
                  <input
                    id="milestone-offset"
                    type="number"
                    step={1}
                    value={draft.offsetDays}
                    onChange={(e) => setDraft({ ...draft, offsetDays: e.target.value })}
                  />
                  <span className="hint">Negative is before, positive after.</span>
                </div>

                <div className="field">
                  <label htmlFor="milestone-anchor">Measured from</label>
                  <select
                    id="milestone-anchor"
                    value={draft.anchor}
                    onChange={(e) =>
                      setDraft({ ...draft, anchor: e.target.value as MilestoneAnchor })
                    }
                  >
                    {MILESTONE_ANCHORS.map((anchor) => (
                      <option key={anchor.value} value={anchor.value}>
                        {anchor.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="milestone-owner">Owner role</label>
                <select
                  id="milestone-owner"
                  value={draft.ownerRole}
                  onChange={(e) => setDraft({ ...draft, ownerRole: e.target.value })}
                >
                  <option value="">Nobody in particular</option>
                  {OWNER_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                  {/*
                    A milestone authored before the roles were fixed can hold a
                    value the list no longer offers. Without this the select
                    would silently show the first option instead, and saving
                    would quietly rewrite the role - so the stored value is
                    offered back until someone deliberately changes it.
                  */}
                  {draft.ownerRole && !OWNER_ROLES.includes(draft.ownerRole as never) ? (
                    <option value={draft.ownerRole}>{draft.ownerRole} (no longer offered)</option>
                  ) : null}
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="primary">
                  {draft.id ? 'Save milestone' : 'Add milestone'}
                </button>
                {draft.id ? (
                  <button type="button" onClick={() => setDraft(emptyDraft)}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        ) : null}

        <div className="editor-content">
          {groups.length === 0 ? (
            <p className="empty">
              No milestones yet.{canEdit && !isPublished ? ' Add the first one on the left.' : ''}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.phase} className="panel">
                <h2>{group.phase}</h2>
                <ul className="card-list">
                  {group.items.map((milestone) => (
                    <li key={milestone.id} className="card">
                      <div className="card-title">{milestone.title}</div>
                      <div className="card-meta">
                        <span>{milestone.offsetLabel}</span>
                        {milestone.ownerRole ? <span>{milestone.ownerRole}</span> : null}
                      </div>
                      {canEdit && !isPublished ? (
                        <div className="card-tags">
                          <button type="button" onClick={() => editExisting(milestone)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              setError(null)
                              try {
                                await deleteMilestone.mutateAsync(milestone.id)
                                await refetch()
                              } catch (err) {
                                report(err)
                              }
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  )
}
